import { ChangeEvent, CSSProperties, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Camera, CheckCircle2, Globe, Lock, Mail, MapPin, Phone, Terminal, User, UserPlus, XCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ALLOWED_IMAGE_TYPES, FileUtils } from '@/utils/fileUtils';
import { ImageCompressor } from '@/utils/imageCompressor';
import { getGoogleAccessToken } from '@/utils/googleAuth';
import { CameraCaptureModal } from '@/components/CameraCaptureModal';
import TermsAcceptance from '@/components/Politica';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type SocialProvider = 'Google' | 'Facebook';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.55-.2-2.27H12v4.3h6.47a5.52 5.52 0 0 1-2.4 3.62v3.01h3.88c2.28-2.1 3.54-5.2 3.54-8.66Z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.07 7.93-2.9l-3.88-3.01c-1.07.72-2.44 1.15-4.05 1.15-3.11 0-5.74-2.1-6.68-4.92H1.31v3.1A12 12 0 0 0 12 24Z"
    />
    <path
      fill="#FBBC05"
      d="M5.32 14.32A7.2 7.2 0 0 1 4.95 12c0-.8.14-1.58.37-2.32v-3.1H1.31A12 12 0 0 0 0 12c0 1.93.46 3.76 1.31 5.42l4.01-3.1Z"
    />
    <path
      fill="#EA4335"
      d="M12 4.77c1.76 0 3.34.6 4.58 1.78l3.43-3.43C17.94 1.17 15.24 0 12 0A12 12 0 0 0 1.31 6.58l4.01 3.1C6.26 6.86 8.89 4.77 12 4.77Z"
    />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      fill="#1877F2"
      d="M24 12A12 12 0 1 0 10.13 23.87V15.5H7.08V12h3.05V9.34c0-3.02 1.8-4.69 4.56-4.69 1.32 0 2.7.24 2.7.24v2.97h-1.52c-1.5 0-1.97.93-1.97 1.89V12h3.35l-.54 3.5h-2.81v8.37A12 12 0 0 0 24 12Z"
    />
    <path fill="#fff" d="m16.71 15.5.54-3.5H13.9V9.75c0-.96.47-1.89 1.97-1.89h1.52V4.89s-1.38-.24-2.7-.24c-2.76 0-4.56 1.67-4.56 4.69V12H7.08v3.5h3.05v8.37a12.08 12.08 0 0 0 3.77 0V15.5h2.81Z" />
  </svg>
);

function passwordStrength(password: string) {
  const checks = {
    minLength: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
  };

  const score = Object.values(checks).filter(Boolean).length;
  const levels = ['Muy debil', 'Debil', 'Media', 'Fuerte', 'Muy fuerte'];
  const colors = ['#ef4444', '#f97316', '#f59e0b', '#22c55e', '#10b981'];

  return {
    score,
    label: levels[score],
    color: colors[score],
    checks,
  };
}

export function RegisterScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, googleAuth, loading, error } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [referredByCode, setReferredByCode] = useState(() => (searchParams.get('ref') || '').trim().toUpperCase());
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [showGoogleCamera, setShowGoogleCamera] = useState(false);
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  useEffect(() => {
    if (!photoFile) {
      setPreview(null);
      return;
    }

    const objectUrl = URL.createObjectURL(photoFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [photoFile]);

  const strength = useMemo(() => passwordStrength(password), [password]);

  const validations = useMemo(
    () => ({
      firstName: firstName.trim().length >= 2,
      lastName: lastName.trim().length >= 2,
      email: emailRegex.test(email.trim()),
      password: strength.score >= 3,
      confirmPassword: password.length > 0 && password === confirmPassword,
    }),
    [firstName, lastName, email, password, confirmPassword, strength.score],
  );

  const isFormValid = Object.values(validations).every(Boolean);

  const handlePhotoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Comprimir la imagen si supera los 50MB (o siempre, para asegurar tamaño)
    try {
      const compressedFile = await ImageCompressor.compressImage(file, 50);
      const validationError = FileUtils.validateFile(compressedFile, ALLOWED_IMAGE_TYPES);
      if (validationError) {
        setLocalError(validationError);
        return;
      }
      setLocalError(null);
      setPhotoFile(compressedFile);
    } catch (err: any) {
      setLocalError('No se pudo comprimir la imagen. ' + (err?.message || ''));
    }
  };

  const handleGoogleRegister = async () => {
    try {
      setInfoMessage(null);
      setLocalError(null);
      const clientId = "845393814372-pcrq4dtk46tetbh3o9kf4hqtskd2agaa.apps.googleusercontent.com";
      const accessToken = await getGoogleAccessToken(clientId);
      setGoogleAccessToken(accessToken);
      setShowGoogleCamera(true);
    } catch (err: any) {
      setLocalError(err?.response?.data?.message || err?.message || 'No se pudo registrar con Google');
    }
  };

  const handleGoogleCameraSubmit = async (capturedPhoto: File) => {
    if (!googleAccessToken) {
      setLocalError('No se pudo validar el token de Google. Intenta nuevamente.');
      return;
    }

    try {
      const profileImageBase64 = await FileUtils.fileToBase64(capturedPhoto);
      await googleAuth({
        accessToken: googleAccessToken,
        referredByCode: referredByCode.trim() || undefined,
        profileImageBase64,
        profileImageName: capturedPhoto.name,
        profileImageMimeType: capturedPhoto.type,
      });

      setShowGoogleCamera(false);
      setGoogleAccessToken(null);
      navigate('/dashboard');
    } catch (err: any) {
      setLocalError(err?.response?.data?.message || err?.message || 'No se pudo registrar con Google');
    }
  };

  const handleSocialRegister = (provider: Exclude<SocialProvider, 'Google'>) => {
    setInfoMessage(`Registro con ${provider} disponible pronto.`);
  };

  const handleRegister = async () => {
    setSubmitted(true);
    setLocalError(null);
    setInfoMessage(null);

    if (!isFormValid) {
      setLocalError('Revisa los campos resaltados para continuar.');
      return;
    }

    try {
      let profileImageBase64: string | undefined;
      let profileImageName: string | undefined;
      let profileImageMimeType: string | undefined;

      if (photoFile) {
        profileImageBase64 = await FileUtils.fileToBase64(photoFile);
        profileImageName = photoFile.name;
        profileImageMimeType = photoFile.type;
      }

      await register({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        phone: phone.trim() || undefined,
        city: city.trim() || undefined,
        country: country.trim() || undefined,
        referredByCode: referredByCode.trim() || undefined,
        profileImageBase64,
        profileImageName,
        profileImageMimeType,
      });

      navigate('/dashboard');
    } catch (registerError) {
      console.error('Register error:', registerError);
    }
  };

  const inputWrapperStyle = (hasError: boolean): CSSProperties => ({
    position: 'relative',
    borderRadius: '12px',
    border: `1px solid ${hasError ? '#fda4af' : '#cbd5e1'}`,
    background: hasError ? '#fff1f2' : '#ffffff',
    transition: 'all 0.2s ease',
  });

  const inputStyle: CSSProperties = {
    width: '100%',
    height: '44px',
    border: 'none',
    outline: 'none',
    borderRadius: '12px',
    padding: '0 12px 0 38px',
    color: '#0f172a',
    fontSize: '14px',
    background: 'transparent',
  };

  const showError = (isValid: boolean, hasValue: boolean) => (submitted || hasValue) && !isValid;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        style={{
          minHeight: '100vh',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background:
            'radial-gradient(circle at 20% 20%, rgba(6, 182, 212, 0.25) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(250, 204, 21, 0.2) 0%, transparent 45%), linear-gradient(135deg, #020617 0%, #0f172a 100%)',
        }}
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15 }}
          style={{
            width: '100%',
            maxWidth: '980px',
            borderRadius: '28px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(15, 23, 42, 0.72)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 24px 60px rgba(2, 6, 23, 0.45)',
          }}
        >
          <section style={{ padding: '28px', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.10)' }}>
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <img src="/logo.png" alt="WingDrive" style={{ height: '106px', width: 'auto', objectFit: 'contain' }} />
          </div>

          <p
            style={{
              display: 'inline-block',
              marginTop: '14px',
              padding: '6px 12px',
              borderRadius: '999px',
              border: '1px solid rgba(251, 191, 36, 0.45)',
              background: 'rgba(251, 191, 36, 0.15)',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: '#fde68a',
            }}
          >
            Registro Workers
          </p>

          <h1 style={{ marginTop: '14px', fontSize: '32px', lineHeight: 1.15, fontWeight: 800 }}>
            Crea tu cuenta y accede a oportunidades reales
          </h1>
          <p style={{ marginTop: '10px', color: '#cbd5e1', fontSize: '15px', maxWidth: '620px' }}>
            Registra tu perfil en minutos, completa tu informacion y recibe acceso a vacantes, herramientas y seguimiento desde un solo lugar.
          </p>
          </section>

          <section style={{ background: '#fff', padding: '24px' }}>
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a' }}>Crear cuenta</h2>
            <p style={{ marginTop: '4px', color: '#475569', fontSize: '14px' }}>Completa tus datos para comenzar. Los campos marcados son obligatorios.</p>

            <div style={{ display: 'grid', gap: '12px', marginTop: '18px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>Nombre</label>
                <div style={inputWrapperStyle(showError(validations.firstName, firstName.length > 0))}>
                  <User size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Ej: Andres" style={inputStyle} />
                </div>
                <p style={{ marginTop: '4px', fontSize: '12px', color: showError(validations.firstName, firstName.length > 0) ? '#dc2626' : '#64748b' }}>
                  {showError(validations.firstName, firstName.length > 0) ? 'Ingresa al menos 2 caracteres.' : 'Como aparece en tu documento.'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>Apellido</label>
                <div style={inputWrapperStyle(showError(validations.lastName, lastName.length > 0))}>
                  <User size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Ej: Molina" style={inputStyle} />
                </div>
                <p style={{ marginTop: '4px', fontSize: '12px', color: showError(validations.lastName, lastName.length > 0) ? '#dc2626' : '#64748b' }}>
                  {showError(validations.lastName, lastName.length > 0) ? 'Ingresa al menos 2 caracteres.' : 'Ayuda a validar mejor tu perfil.'}
                </p>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>Correo electronico</label>
                <div style={inputWrapperStyle(showError(validations.email, email.length > 0))}>
                  <Mail size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="worker@wingdrive.com" style={inputStyle} />
                </div>
                <p style={{ marginTop: '4px', fontSize: '12px', color: showError(validations.email, email.length > 0) ? '#dc2626' : '#64748b' }}>
                  {showError(validations.email, email.length > 0) ? 'Usa un correo valido para activar tu cuenta.' : 'Te enviaremos notificaciones de tu proceso.'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>Contrasena</label>
                <div style={inputWrapperStyle(showError(validations.password, password.length > 0))}>
                  <Lock size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimo 8 caracteres" style={inputStyle} />
                </div>

                <div style={{ marginTop: '8px' }}>
                  <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${(strength.score / 4) * 100}%`,
                        height: '100%',
                        background: strength.color,
                        transition: 'all 0.2s ease',
                      }}
                    />
                  </div>
                  <p style={{ marginTop: '6px', fontSize: '12px', color: strength.color, fontWeight: 700 }}>Fortaleza: {strength.label}</p>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>Confirmar contrasena</label>
                <div style={inputWrapperStyle(showError(validations.confirmPassword, confirmPassword.length > 0))}>
                  <Lock size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repite la contrasena" style={inputStyle} />
                </div>
                <p style={{ marginTop: '4px', fontSize: '12px', color: showError(validations.confirmPassword, confirmPassword.length > 0) ? '#dc2626' : '#64748b' }}>
                  {showError(validations.confirmPassword, confirmPassword.length > 0) ? 'Las contrasenas no coinciden.' : 'Debe coincidir exactamente con la anterior.'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>Telefono (opcional)</label>
                <div style={inputWrapperStyle(false)}>
                  <Phone size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Ej: 3001234567" style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>Ciudad (opcional)</label>
                <div style={inputWrapperStyle(false)}>
                  <MapPin size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ej: Bogota" style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>Pais (opcional)</label>
                <div style={inputWrapperStyle(false)}>
                  <Globe size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Ej: Colombia" style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>Codigo de referido (opcional)</label>
                <div style={inputWrapperStyle(false)}>
                  <UserPlus size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input
                    value={referredByCode}
                    onChange={(e) => setReferredByCode(e.target.value.toUpperCase())}
                    placeholder="Ej: ABC123"
                    style={inputStyle}
                  />
                </div>
                <p style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>Si te invitaron, ingresa el codigo para vincular la referencia.</p>
              </div>
            </div>

            <div
              style={{
                marginTop: '14px',
                borderRadius: '14px',
                border: '1px dashed #cbd5e1',
                background: '#f8fafc',
                padding: '12px',
              }}
            >
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                <Camera size={15} />
                Foto de perfil (opcional)
              </label>
              <p style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>Puedes tomarla con la camara de tu dispositivo.</p>
              <input type="file" accept="image/*" capture="user" onChange={handlePhotoChange} style={{ marginTop: '10px', width: '100%' }} />

              {preview && (
                <img
                  src={preview}
                  alt="Foto de perfil"
                  style={{ marginTop: '10px', width: '88px', height: '88px', borderRadius: '999px', objectFit: 'cover', border: '2px solid #22d3ee' }}
                />
              )}
            </div>

            <div style={{ marginTop: '14px', display: 'grid', gap: '7px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: strength.checks.minLength ? '#16a34a' : '#64748b' }}>
                {strength.checks.minLength ? <CheckCircle2 size={14} /> : <XCircle size={14} />} Minimo 8 caracteres
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: strength.checks.hasUpper ? '#16a34a' : '#64748b' }}>
                {strength.checks.hasUpper ? <CheckCircle2 size={14} /> : <XCircle size={14} />} Al menos una mayuscula
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: strength.checks.hasLower ? '#16a34a' : '#64748b' }}>
                {strength.checks.hasLower ? <CheckCircle2 size={14} /> : <XCircle size={14} />} Al menos una minuscula
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: strength.checks.hasNumber ? '#16a34a' : '#64748b' }}>
                {strength.checks.hasNumber ? <CheckCircle2 size={14} /> : <XCircle size={14} />} Al menos un numero
              </div>
            </div>

            {(localError || error) && (
              <p style={{ marginTop: '12px', fontSize: '13px', color: '#b91c1c', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '10px', padding: '8px 10px' }}>
                {localError || error}
              </p>
            )}

            {infoMessage && (
              <p style={{ marginTop: '12px', fontSize: '13px', color: '#0f766e', background: '#ccfbf1', border: '1px solid #99f6e4', borderRadius: '10px', padding: '8px 10px' }}>
                {infoMessage}
              </p>
            )}
            <TermsAcceptance value={termsAccepted} onChange={setTermsAccepted} />
            <button
              onClick={handleRegister}
              disabled={loading || !isFormValid || !termsAccepted}
              style={{
                width: '100%',
                marginTop: '14px',
                height: '46px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(90deg, #f59e0b, #facc15)',
                color: '#0f172a',
                fontWeight: 800,
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: loading || !isFormValid || !termsAccepted ? 'not-allowed' : 'pointer',
                opacity: loading || !isFormValid || !termsAccepted ? 0.6 : 1,
              }}
            >
              <UserPlus size={16} />
              {loading ? 'Creando cuenta...' : 'Crear mi cuenta'}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
              <span style={{ height: '1px', background: '#e2e8f0', flex: 1 }} />
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                o registrate con
              </span>
              <span style={{ height: '1px', background: '#e2e8f0', flex: 1 }} />
            </div>

            <div style={{ display: 'grid', gap: '10px' }}>
              <button
                type="button"
                onClick={handleGoogleRegister}
                style={{
                  height: '44px',
                  borderRadius: '12px',
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#334155',
                  fontSize: '14px',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                <GoogleIcon />
                Registrarse con Google
              </button>

              <button
                type="button"
                onClick={() => handleSocialRegister('Facebook')}
                style={{
                  height: '44px',
                  borderRadius: '12px',
                  border: '1px solid #1877F2',
                  background: '#1877F2',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                <FacebookIcon />
                Registrarse con Facebook
              </button>
            </div>

            <p style={{ marginTop: '14px', textAlign: 'center', fontSize: '14px', color: '#475569' }}>
              Ya tienes cuenta?{' '}
              <button
                type="button"
                onClick={() => navigate('/auth')}
                style={{ border: 'none', background: 'transparent', color: '#0369a1', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
              >
                Inicia sesion
              </button>
            </p>
          </div>
          </section>
        </motion.div>
      </motion.div>
      <CameraCaptureModal
        open={showGoogleCamera}
        loading={loading}
        title="Foto obligatoria para Google"
        description="Para registro/inicio con Google debes tomar una foto en este momento. Se ignora la foto del perfil de Google."
        onCancel={() => {
          setShowGoogleCamera(false);
          setGoogleAccessToken(null);
        }}
        onCaptured={handleGoogleCameraSubmit}
      />
    </>
  );
}
